/** Predefined brand-color palette shared between org creation and filtering. */
export const BRAND_COLORS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "#1e3a8a", label: "Navy" },
  { value: "#991b1b", label: "Crimson" },
  { value: "#0f766e", label: "Teal" },
  { value: "#166534", label: "Forest" },
  { value: "#92400e", label: "Amber" },
  { value: "#6d28d9", label: "Violet" },
  { value: "#1d4ed8", label: "Blue" },
  { value: "#3f6212", label: "Olive" },
  { value: "#1e293b", label: "Slate" },
];

/** Maps a palette hex value to its `iam.brandColors.*` translation key. */
const BRAND_COLOR_TRANSLATION_KEYS: Record<string, string> = {
  "#1e3a8a": "navy",
  "#991b1b": "crimson",
  "#0f766e": "teal",
  "#166534": "forest",
  "#92400e": "amber",
  "#6d28d9": "violet",
  "#1d4ed8": "blue",
  "#3f6212": "olive",
  "#1e293b": "slate",
};

/** Translates a palette color's display label; custom hex values fall back to the untranslated label (usually the hex itself). */
export function translateBrandColorLabel(
  t: (key: string, defaultValue: string) => string,
  value: string,
  fallbackLabel: string,
): string {
  const key = BRAND_COLOR_TRANSLATION_KEYS[value];
  return key ? t(`iam.brandColors.${key}`, fallbackLabel) : fallbackLabel;
}

const BRAND_COLOR_MAP = new Map(BRAND_COLORS.map((c) => [c.value, c.label]));

/** Resolves a hex color to its human-readable label, falling back to the hex value. */
export function brandColorLabel(hex: string): string {
  return BRAND_COLOR_MAP.get(hex) ?? hex;
}

/** Filter key for explicit brand colors only; null = org uses auto accent (not filterable). */
export function tenantBrandColorFilterKey(t: { brandColor?: string | null }): string | null {
  const value = t.brandColor?.trim();
  return value || null;
}

/** Full color-filter menu: palette + any custom hex in use. */
export function buildBrandColorFilterOptions(
  tenants: ReadonlyArray<{ brandColor?: string | null }> = [],
): Array<{ value: string; label: string }> {
  const paletteValues = new Set(BRAND_COLORS.map((c) => c.value));
  const options: Array<{ value: string; label: string }> = BRAND_COLORS.map((c) => ({
    value: c.value,
    label: c.label,
  }));

  for (const t of tenants) {
    const value = t.brandColor?.trim();
    if (!value || paletteValues.has(value)) continue;
    if (!options.some((o) => o.value === value)) {
      options.push({ value, label: brandColorLabel(value) });
    }
  }

  return options.sort((a, b) => a.label.localeCompare(b.label));
}
